import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import moment from 'moment';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import Spinner from '~/components/Spinner/Spinner';
import { fetchRetentionQaReport, updateRetentionPendingStatus, updateOwnerGroupRetentionPendingStatus } from '~/api/analytics';
import { RootState } from '~/redux/store';
import { Roles } from '~/enums/Roles';
import NotFound from '~/components/NotFound/NotFound';
import GenericModal from '~/components/GenericModal/GenericModal';
import type { RetentionQAProperty, RetentionQAOutlier, RetentionQAOwnerGroupRow } from '~/types/analytics';
import styles from './RetentionQAReport.module.css';

const OUTLIER_LABELS: Record<string, string> = {
  COBU_BELOW_NON_COBU: 'Cobu rate below Non-Cobu',
  TOTAL_OUTSIDE_RANGE: 'Total rate below 40%',
  TOTAL_RATE_OUT_OF_RANGE: 'Total rate below 40%',
  LOW_COBU_SAMPLE: 'Low Cobu sample (<20 expiries)'
};

const formatPct = (v: number) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return `${Math.round(v * 100)}%`;
};

const OutlierBadge = ({ outlier }: { outlier: RetentionQAOutlier }) => (
  <span className={styles.badge} title={outlier.message}>
    {OUTLIER_LABELS[outlier.code] ?? outlier.code}
  </span>
);

const RetentionQAReport = () => {
  const userRole = useSelector((state: RootState) => state.auth.userRole);
  const queryClient = useQueryClient();

  const defaultMonth = moment().subtract(1, 'month').format('YYYY-MM');
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [dataType, setDataType] = useState<'buildings' | 'ownerGroups'>('buildings');
  const [pendingTarget, setPendingTarget] = useState<{ buildingId: string; buildingName: string; newValue: boolean } | null>(null);
  const [ownerGroupPendingTarget, setOwnerGroupPendingTarget] = useState<{ ownerGroupId: string; ownerGroupName: string; newValue: boolean } | null>(null);

  const {
    data: reportRes,
    isLoading,
    isError
  } = useQuery(
    ['retentionQaReport', selectedMonth, dataType],
    () => fetchRetentionQaReport(`${selectedMonth}-01`, dataType),
    { refetchOnWindowFocus: false, enabled: !!selectedMonth }
  );

  const { mutate: updatePending } = useMutation(
    ({ buildingId, newValue }: { buildingId: string; newValue: boolean }) =>
      updateRetentionPendingStatus(buildingId, `${selectedMonth}-01`, newValue),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['retentionQaReport', selectedMonth]);
        setPendingTarget(null);
      }
    }
  );

  const { mutate: updateOwnerGroupPending } = useMutation(
    ({ ownerGroupId, newValue }: { ownerGroupId: string; newValue: boolean }) =>
      updateOwnerGroupRetentionPendingStatus(ownerGroupId, `${selectedMonth}-01`, newValue),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['retentionQaReport', selectedMonth]);
        setOwnerGroupPendingTarget(null);
      }
    }
  );

  if (userRole !== Roles.Superadmin && userRole !== Roles.Vcm) {
    return <NotFound />;
  }

  const report = reportRes?.data;
  const isBuildingsView = dataType === 'buildings';

  const buildingsReport = isBuildingsView ? (report as { totalProperties: number; outlierCount: number; properties: RetentionQAProperty[] } | undefined) : undefined;
  const ownerGroupsReport = !isBuildingsView ? (report as { totalOwnerGroups: number; outlierCount: number; ownerGroups: RetentionQAOwnerGroupRow[] } | undefined) : undefined;

  const totalCount = isBuildingsView ? buildingsReport?.totalProperties : ownerGroupsReport?.totalOwnerGroups;
  const outlierCount = report?.outlierCount ?? 0;
  const totalCountLabel = isBuildingsView ? 'Properties with Data' : 'Owner Groups with Data';

  const monthOptions = Array.from({ length: 24 }, (_, i) => {
    const m = moment().subtract(i, 'months');
    return { value: m.format('YYYY-MM'), label: m.format('MMMM YYYY') };
  });

  return (
    <div className='content-container'>
      <Breadcrumbs />

      <div className={styles.header}>
        <h1 className={styles.title}>Retention QA Report</h1>
        <div className={styles.controls}>
          <label className={styles.monthLabel} htmlFor='qa-month-picker'>
            Month
          </label>
          <select
            id='qa-month-picker'
            className={styles.monthPicker}
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {monthOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${isBuildingsView ? styles.tabActive : ''}`}
          onClick={() => setDataType('buildings')}
        >
          Buildings
        </button>
        <button
          className={`${styles.tab} ${!isBuildingsView ? styles.tabActive : ''}`}
          onClick={() => setDataType('ownerGroups')}
        >
          Owner Groups
        </button>
      </div>

      {isLoading && (
        <div className={styles.spinnerWrap}>
          <Spinner />
        </div>
      )}

      {isError && (
        <p className={styles.errorMsg}>Failed to load report. Please try again.</p>
      )}

      {!isLoading && !isError && report && (
        <>
          <div className={styles.summary}>
            <div className={styles.summaryCard}>
              <div className={styles.summaryValue}>{totalCount}</div>
              <div className={styles.summaryLabel}>{totalCountLabel}</div>
            </div>
            <div className={`${styles.summaryCard} ${outlierCount > 0 ? styles.summaryCardWarn : ''}`}>
              <div className={styles.summaryValue}>{outlierCount}</div>
              <div className={styles.summaryLabel}>Outlier Flags</div>
            </div>
          </div>

          <div className={styles.legendWrap}>
            <strong>Outlier flags:</strong>
            <span className={styles.legendItem}>
              <span className={styles.badge}>{OUTLIER_LABELS.COBU_BELOW_NON_COBU}</span>
              — Cobu retention rate is lower than Non-Cobu rate
            </span>
            <span className={styles.legendItem}>
              <span className={styles.badge}>{OUTLIER_LABELS.TOTAL_OUTSIDE_RANGE}</span>
              — Total retention rate falls below 40%
            </span>
            <span className={styles.legendItem}>
              <span className={styles.badge}>{OUTLIER_LABELS.LOW_COBU_SAMPLE}</span>
              — Fewer than 20 Cobu expiring leases (small sample)
            </span>
          </div>

          <div className={styles.pendingLegend}>
            <strong>Pending:</strong>
            {isBuildingsView ? (
              <>{' '}Properties with value <strong>pending = true</strong> have analysis data that is not yet confirmed — their retention stats will not appear on the building details page and will not be included in monthly reports.
              <br />You can toggle the pending status by clicking the <strong>Pending</strong> value in the table.</>
            ) : (
              <>{' '}Owner groups show how many of their buildings are pending (<strong>pending / total</strong>). Buildings marked as pending have unconfirmed data — their retention stats are excluded from the aggregation and will not be included in monthly reports.
              <br />Clicking the <strong>Pending</strong> value bulk-updates all buildings in the owner group to the new pending state.</>
            )}
          </div>

          <div className={styles.tableWrap}>
            {isBuildingsView ? (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.thBuilding}>Property</th>
                    <th className={styles.thBuilding}>Owner Group</th>
                    <th>Data From</th>
                    <th colSpan={3} className={styles.thGroup}>Cobu Users</th>
                    <th colSpan={3} className={styles.thGroup}>Non-Cobu Users</th>
                    <th colSpan={3} className={styles.thGroup}>Total (All)</th>
                    <th>Flags</th>
                    <th>Pending</th>
                  </tr>
                  <tr className={styles.subHeaderRow}>
                    <th />
                    <th />
                    <th />
                    <th>Expiries</th>
                    <th>Retained</th>
                    <th>Rate</th>
                    <th>Expiries</th>
                    <th>Retained</th>
                    <th>Rate</th>
                    <th>Expiries</th>
                    <th>Retained</th>
                    <th>Rate</th>
                    <th />
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {buildingsReport?.properties.map((prop: RetentionQAProperty) => (
                    <tr
                      key={prop.buildingId}
                      className={
                        prop.isPending
                          ? styles.pendingRow
                          : prop.isOutlier
                          ? styles.outlierRow
                          : ''
                      }
                    >
                      <td className={styles.buildingCell}>
                        <Link to={`/building/${prop.buildingId}`} className={styles.tableLink}>
                          {prop.buildingName}
                        </Link>
                      </td>
                      <td className={styles.buildingCell}>
                        {prop.ownerGroupId && prop.ownerGroupName ? (
                          <Link
                            to={`/building/assetOverview?ownerGroupId=${prop.ownerGroupId}&ownerGroupName=${encodeURIComponent(prop.ownerGroupName)}&isOwnerGroup=true`}
                            className={styles.tableLink}
                          >
                            {prop.ownerGroupName}
                          </Link>
                        ) : '—'}
                      </td>
                      <td className={styles.centeredCell}>
                        {prop.dataStartMonth ?? '—'}
                      </td>
                      <td className={styles.centeredCell}>{prop.cobuExpiries}</td>
                      <td className={styles.centeredCell}>{prop.cobuRetained}</td>
                      <td className={`${styles.centeredCell} ${styles.rateCell}`}>
                        {formatPct(prop.cobuRetentionRate)}
                      </td>
                      <td className={styles.centeredCell}>{prop.nonCobuExpiries}</td>
                      <td className={styles.centeredCell}>{prop.nonCobuRetained}</td>
                      <td className={`${styles.centeredCell} ${styles.rateCell}`}>
                        {formatPct(prop.nonCobuRetentionRate)}
                      </td>
                      <td className={styles.centeredCell}>{prop.totalExpiries}</td>
                      <td className={styles.centeredCell}>{prop.totalRetained}</td>
                      <td className={`${styles.centeredCell} ${styles.rateCell}`}>
                        {formatPct(prop.totalRetentionRate)}
                      </td>
                      <td className={styles.flagsCell}>
                        {prop.outliers.map((o) => (
                          <OutlierBadge key={o.code} outlier={o} />
                        ))}
                      </td>
                      <td className={styles.centeredCell}>
                        <button
                          className={`table-button pointer ${prop.isPending ? styles.pendingBtnTrue : ''}`}
                          onClick={() => setPendingTarget({ buildingId: prop.buildingId, buildingName: prop.buildingName, newValue: !prop.isPending })}
                        >
                          {prop.isPending ? 'true' : 'false'}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(buildingsReport?.properties.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={14} className={styles.noData}>
                        No properties with published retention data for this month. Try changing the month in the month picker above.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.thBuilding}>Owner Group</th>
                    <th colSpan={3} className={styles.thGroup}>Cobu Users</th>
                    <th colSpan={3} className={styles.thGroup}>Non-Cobu Users</th>
                    <th colSpan={3} className={styles.thGroup}>Total (All)</th>
                    <th>Flags</th>
                    <th>Buildings</th>
                    <th>Pending</th>
                  </tr>
                  <tr className={styles.subHeaderRow}>
                    <th />
                    <th>Expiries</th>
                    <th>Retained</th>
                    <th>Rate</th>
                    <th>Expiries</th>
                    <th>Retained</th>
                    <th>Rate</th>
                    <th>Expiries</th>
                    <th>Retained</th>
                    <th>Rate</th>
                    <th />
                    <th>included / total</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {ownerGroupsReport?.ownerGroups?.map((og: RetentionQAOwnerGroupRow) => (
                    <tr
                      key={og.ownerGroupId}
                      className={
                        og.isPending
                          ? styles.pendingRow
                          : og.isOutlier
                          ? styles.outlierRow
                          : ''
                      }
                    >
                      <td className={styles.buildingCell}>
                        <Link
                          to={`/building/assetOverview?ownerGroupId=${og.ownerGroupId}&ownerGroupName=${encodeURIComponent(og.ownerGroupName)}&isOwnerGroup=true`}
                          className={styles.tableLink}
                        >
                          {og.ownerGroupName}
                        </Link>
                      </td>
                      <td className={styles.centeredCell}>{og.cobuExpiries}</td>
                      <td className={styles.centeredCell}>{og.cobuRetained}</td>
                      <td className={`${styles.centeredCell} ${styles.rateCell}`}>
                        {formatPct(og.cobuRetentionRate)}
                      </td>
                      <td className={styles.centeredCell}>{og.nonCobuExpiries}</td>
                      <td className={styles.centeredCell}>{og.nonCobuRetained}</td>
                      <td className={`${styles.centeredCell} ${styles.rateCell}`}>
                        {formatPct(og.nonCobuRetentionRate)}
                      </td>
                      <td className={styles.centeredCell}>{og.totalExpiries}</td>
                      <td className={styles.centeredCell}>{og.totalRetained}</td>
                      <td className={`${styles.centeredCell} ${styles.rateCell}`}>
                        {formatPct(og.totalRetentionRate)}
                      </td>
                      <td className={styles.flagsCell}>
                        {og.outliers.map((o) => (
                          <OutlierBadge key={o.code} outlier={o} />
                        ))}
                      </td>
                      <td className={styles.centeredCell}>
                        {og.buildingCount} / {og.totalBuildingCount}
                      </td>
                      <td className={styles.centeredCell}>
                        <button
                          className={`table-button pointer ${og.isPending ? styles.pendingBtnTrue : ''}`}
                          onClick={() => setOwnerGroupPendingTarget({ ownerGroupId: og.ownerGroupId, ownerGroupName: og.ownerGroupName, newValue: !og.isPending })}
                        >
                          {og.pendingCount} / {og.totalBuildingCount}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(ownerGroupsReport?.ownerGroups?.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={13} className={styles.noData}>
                        No owner groups with published retention data for this month. Try changing the month in the month picker above.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
      <GenericModal
        showModal={!!pendingTarget}
        title='Update Pending Status'
        content={
          pendingTarget
            ? pendingTarget.newValue
              ? `Mark "${pendingTarget.buildingName}" as pending? Retention data will not be visible on the building details page anymore and will not be included in monthly reports.`
              : `Mark "${pendingTarget.buildingName}" as not pending? Retention data will become visible on the building details page and will be included in monthly reports.`
            : ''
        }
        actionText='Confirm'
        actionFunction={() => {
          if (pendingTarget) {
            updatePending({ buildingId: pendingTarget.buildingId, newValue: pendingTarget.newValue });
          }
        }}
        onChange={(show) => { if (!show) setPendingTarget(null); }}
      />
      <GenericModal
        showModal={!!ownerGroupPendingTarget}
        title='Update Pending Status'
        content={
          ownerGroupPendingTarget
            ? ownerGroupPendingTarget.newValue
              ? `Mark "${ownerGroupPendingTarget.ownerGroupName}" as pending? Retention data for all buildings in this owner group will not be visible on building details pages and will not be included in monthly reports.`
              : `Mark "${ownerGroupPendingTarget.ownerGroupName}" as not pending? Retention data for all buildings in this owner group will become visible on building details pages and will be included in monthly reports.`
            : ''
        }
        actionText='Confirm'
        actionFunction={() => {
          if (ownerGroupPendingTarget) {
            updateOwnerGroupPending({ ownerGroupId: ownerGroupPendingTarget.ownerGroupId, newValue: ownerGroupPendingTarget.newValue });
          }
        }}
        onChange={(show) => { if (!show) setOwnerGroupPendingTarget(null); }}
      />
    </div>
  );
};

export default RetentionQAReport;
